<template>
  <div>
    <AuthHeader
      :title="props.isReset ? t('Задайте новый пароль') : t('Придумайте пароль')"
      :subtitle="t('Придумайте сложный пароль, содержащий строчные и прописные буквы, а так же цифры и символы')"
    />
    <BaseForm
      v-model="formModel"
      :schema="[
        {
          type: FormField.password,
          name: 'password',
          label: t('Введите пароль'),
          attrs: { 'data-testid': 'auth-password-input' },
          required: true,
        },
        {
          type: FormField.repeatPassword,
          name: 'repeatPassword',
          label: t('Повторите пароль'),
          attrs: { 'data-testid': 'auth-repeat-password-input' },
          required: true,
        },
      ]"
      :when-handle-submit="setPassword"
    >
      <template #actions="{ nextTabindex }">
        <BaseButton
          with-full
          with-decor
          data-testid="auth-prev-btn"
          @click="emit('back')"
        >
          {{ t('Назад') }}
        </BaseButton>
        <BaseButton
          type="submit"
          with-decor
          with-full
          variant="primary"
          data-testid="auth-next-btn"
          :tabindex="nextTabindex"
        >
          {{ t('Готово') }}
        </BaseButton>
      </template>
    </BaseForm>
    <AuthContactDevelopersBlock class="auth-set-password-form__developers" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import {
  AuthContactDevelopersBlock,
  useAccountStore,
  useAuth,
} from '~/entities/auth'
import {
  useRegisterUserMutation,
  useSetNewPasswordAfterResetMutation,
} from '~/shared/api'
import { FormField, REGEX_FOR_CHECK_PHONE } from '~/shared/const'
import { useNotificationStore } from '~/shared/lib'
import { BaseButton, BaseForm } from '~/shared/ui'
import i18n from '../model/AuthSetPasswordForm.i18n.json'

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'success', isReset: boolean): void
  (e: 'back'): void
}>()

interface Props {
  isReset: boolean
  method: 'phone' | 'email'
}

const { t } = useI18n(i18n)
const { notify } = useNotificationStore()
const { login } = useAuth()
const { mutate: registerUserMutation } = useRegisterUserMutation()
const { mutate: setNewPasswordMutation } = useSetNewPasswordAfterResetMutation()
const accountStore = useAccountStore()
const {
  data: savedProfile,
  phone,
  phoneOrEmail,
  verifiedRegisterCode,
  verifiedResetCode,
} = storeToRefs(accountStore)
const { resetAllExceptIdentifier: resetSavedForm } = accountStore

const formModel = ref({ password: '', repeatPassword: '' })

onMounted(() => {
  if (props.isReset && !verifiedResetCode.value) {
    notify({ message: 'Вы не подтвердили код верификации', type: 'error' })
  }
  if (!props.isReset && !verifiedRegisterCode.value) {
    notify({ message: 'Вы не подтвердили код верификации', type: 'error' })
  }
})

function resetPassword(newPassword: string) {
  setNewPasswordMutation({
    identifier: phoneOrEmail.value,
    newPassword,
  }).then(() => {
    resetSavedForm()
    verifiedResetCode.value = false
    emit('success', true)
  })
}

function registerUser(password: string) {
  registerUserMutation({
    input: {
      email: savedProfile.value.email || undefined,
      firstName: savedProfile.value.firstName,
      middleName: savedProfile.value.middleName,
      lastName: savedProfile.value.lastName,
      password,
      birthday: savedProfile.value.birthday,
      phone: phone.value,
    },
  })
    .then(() => {
      const isPhone = REGEX_FOR_CHECK_PHONE.test(phoneOrEmail.value)
      return login(phoneOrEmail.value, password, isPhone ? 'phone' : 'email')
    })
    .then(() => {
      resetSavedForm()
      verifiedRegisterCode.value = false
      emit('success', false)
    })
}

async function setPassword({ password }: { password: string }) {
  if (props.isReset) {
    resetPassword(password)
  }
  else {
    registerUser(password)
  }
}
</script>

<style lang="scss" scoped>
.auth-set-password-form__developers {
  margin-top: rem(21px);
}
</style>
